import { Box, Card, CardContent, Typography } from '@mui/material'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import type { ComponentType } from 'react'

import { useCountUp } from '@/shared/hooks/useCountUp'

import { formatIncentivesUsd, formatMetricCount } from '../lib/format-dashboard-metric'

const valueTypographySx = {
  fontSize: 30,
  fontWeight: 700,
  lineHeight: '36px',
  letterSpacing: '0.4px',
  color: '#0f172a',
  mb: 1,
} as const

const iconWrapSx = {
  width: 48,
  height: 48,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  mb: 2,
} as const

export type DashboardMetricValueFormat = 'count' | 'currency'

function formatAnimatedValue(
  animated: number,
  valueFormat: DashboardMetricValueFormat,
): string {
  if (valueFormat === 'currency') return formatIncentivesUsd(animated)
  return formatMetricCount(animated)
}

export function DashboardMetricCard({
  'data-testid': dataTestId,
  cardBg,
  borderColor = '#e2e8f0',
  iconBg,
  Icon,
  value,
  valueFormat = 'count',
  label,
  labelColor,
}: Readonly<{
  'data-testid'?: string
  cardBg: string
  borderColor?: string
  iconBg: string
  Icon: ComponentType<SvgIconProps>
  /** Raw metric — animated from 0 like Platform Admin tiles. */
  value: number
  valueFormat?: DashboardMetricValueFormat
  label: string
  labelColor: string
}>) {
  const animated = useCountUp(Number.isFinite(value) ? value : 0, 700, 0)
  const display = formatAnimatedValue(animated, valueFormat)

  return (
    <Card
      data-testid={dataTestId}
      elevation={0}
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: '10px',
        minHeight: 174,
        bgcolor: cardBg,
      }}
    >
      <CardContent sx={{ px: 3, pt: 3, pb: 3 }}>
        <Box sx={{ ...iconWrapSx, bgcolor: iconBg }}>
          <Icon sx={{ color: '#fff', fontSize: 24 }} aria-hidden />
        </Box>
        <Typography variant="h3" component="p" sx={valueTypographySx}>
          {display}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            color: labelColor,
            letterSpacing: '-0.15px',
          }}
        >
          {label}
        </Typography>
      </CardContent>
    </Card>
  )
}
